import type { ProductInfoInput } from '@@partner:graphql/mutation/catalog/saveProduct.gql'
import type { Product, RemoteFile } from '@@partner:types:app'
import type { ProductFormGalleryItem, ProductFormState } from './types'

import { mergeLeft, mergeLeftDropping } from 'merge-left-utils'
import { groupBy, map, merge, prop } from 'rambda'

import { PRODUCT_STATUS_ENABLED } from '@@partner:config/product'
import { GQL_ID } from '@@partner:types:graphql'

export const PRODUCT_TRANSLATION_DEFAULT_STATE = {
  name: '',
  short_description: '',
  pkg: '',
  ingredients: '',
  nutval: '',
  manufacturer: '',
  country: '',
  volume: '',
  language_code: '',
  storage_conditions: '',
}

export const PRODUCT_DEFAULT_STATE: ProductFormState = {
  id: undefined,
  xid: undefined,
  article_num: '',
  name: '',
  weight: 0,
  weight_netto: 0,
  min_count: 0,
  max_amount: 0,
  count_multiplier: 1,
  status: PRODUCT_STATUS_ENABLED,
  w: undefined,
  l: undefined,
  h: undefined,
  volume: undefined,
  slug: '',
  pkg: '',
  ingredients: '',
  nutval: '',
  short_description: '',
  brand_id: undefined,
  brand: {
    id: undefined,
    name: ''
  },
  price_type: 'piece',
  prices: [],
  preview_id: undefined,
  preview: {
    url: undefined
  },
  galleryImages: [],
  galleryImageIds: [],
  shop_ids: [],

  marketingLabelIds: [],
  marketingLabels: [],

  // stock
  useSingleStock: false,
  singleStock: 0,
  stocks: [],

  categoryIds: [],
  categoryChains: [],

  eans: [],
  
  markable: false,
  markCodeType: '',
  
  indexable: true,
  excisable: false,
  is_private_label: false,
  is_own_brand: false,
  is_alcohol: false,
  manufacturer: '',

  languages: [],
  i18n: [],

  priority: 20,

  fats: '',
  proteins: '',
  carbohydrates: '',
  calories: '',

  shelfLife: null,
  manufacturerName: ''
}

export const makeProductFormState = (product?: Product): ProductFormState => {
  if (product) {
    const languages = product.i18n.map(({ language_code }) => language_code)
    const marketingLabels = product?.marketingLabels ?? []

    const imagesIdsById = product.imagesIds ? groupBy(prop('id'), product.imagesIds) : null
    const galleryImages: ProductFormGalleryItem[] = product.images && imagesIdsById ? map((image: RemoteFile) => merge(image, imagesIdsById[image.id][0]), product.images) : []

    const preparedTranslations = product.i18n.map((translation) => mergeLeft(PRODUCT_TRANSLATION_DEFAULT_STATE, translation))

    return mergeLeft<ProductFormState>(PRODUCT_DEFAULT_STATE, {
      ...product,
      languages,
      fats: product.productProperties?.fats ?? '',
      proteins: product.productProperties?.proteins ?? '',
      carbohydrates: product.productProperties?.carbohydrate ?? '',
      calories: product.productProperties?.calories ?? '',
      manufacturerName: product.productProperties?.manufacturerName ?? '',
      shelfLife: product.productProperties?.shelfLife ?? null,

      i18n: preparedTranslations,
      galleryImages,
      galleryImageIds: galleryImages.map(image => image.id),
      marketingLabelIds: marketingLabels.map((label) => {
        return label.id
      })
    })
  }
  
  return PRODUCT_DEFAULT_STATE
  
}

// state and api variable are same excluding info fields
// so drop them and go
export const mapStateToApiVariable = (
  state: ProductFormState
): ProductInfoInput => {
  const mergeResult = mergeLeftDropping(
    [ 'prices', 'brand', 'preview', 'galleryImages', 'shop', 'categoryChains', 'marketingLabels', 'languages', 'fats', 'proteins', 'carbohydrates', 'calories', 'manufacturerName', 'shelfLife' ],
    PRODUCT_DEFAULT_STATE,
    {
      ...state,
      count_multiplier: Number(state.count_multiplier),
      min_count: Number(state.min_count),
      max_amount: Number(state.max_amount),
      weight: Number(state.weight),
      weight_netto: Number(state.weight_netto),
      w: Number(state.w),
      h: Number(state.h),
      l: Number(state.l),
      volume: `${Number(state.volume)}`,
      galleryImageIds: state.galleryImages.map(image => image.id),
      i18n: state.i18n,
      stocks: state.stocks.map(stock => {
        return {
          shop_id: stock.shop_id,
          amount: Number(stock.amount)
        }
      }),
      priority: Number(state.priority)
    }
  )

  const result = {
    ...mergeResult,
    productProperties: {
      fats: state.fats,
      proteins: state.proteins,
      carbohydrates: state.carbohydrates,
      calories: state.calories,
      manufacturerName: state.manufacturerName,
      shelfLife: state.shelfLife && state.shelfLife > 0 ? `${state.shelfLife}` : null
    }
  }

  return result
}

export const mapApiDataToState = (data: Product): ProductFormState =>
  makeProductFormState(data)
